// CreateSoftTags page for multi-step soft tag creation
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import routes from '@/app/routes';
import {
  Headline,
  IconButton,
  Separator,
  Title,
  Caption,
  ProgressTracker,
} from '@yaradigitallabs/ahua-react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { EditSoftTagSkeleton } from '@/components/skeleton';
import { useNotification } from '@/components/notification/useNotification';
import type { Tags } from '@/features/tags/types/tags.types';

import { progressTrackerSteps, stepsText } from './utils/constants';
import { Details } from './component/Details';
import { Formula } from './component/Formula';
import { Review } from './component/Review';
import { createSoftTag, editSoftTag } from './store/createSoftTag.thunk';
import type { SoftTagState } from './utils/types';
import { mapCreateSoftTagStateToParams, setSchedule } from './utils/helpers';
import { getSoftTag } from '../tags/store/tags.thunk';

// Main component for creating a new soft tag
export const CreateSoftTags = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [activeStep, setActiveStep] = useState(1);
  const [softTagDetails, setSoftTagDetails] = useState<SoftTagState | null>(
    null
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const { softTag, getSoftTagLoading } = useAppSelector(state => state.tags);
  const notification = useNotification();

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const step = event.state?.step;
      if (step) {
        setActiveStep(step);
      } else {
        navigate(routes.LIST_TAGS);
      }
    };
    window.history.pushState({ step: 1 }, '');
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);
  // Get navigation data and print it
  useEffect(() => {
    const fetchData = async () => {
      const navigationData = location.state as { softTagData?: Tags };
      if (navigationData?.softTagData) {
        setIsEditMode(true);
        dispatch(getSoftTag({ id: navigationData.softTagData.id }));
      }
    };
    fetchData();
  }, [location.state]);

  useEffect(() => {
    if (softTag) {
      setSoftTagDetails(softTag);
    }
  }, [softTag]);

  // Handles confirmation of the first step and moves to next
  const handleFirstStep = (values: any) => {
    if (values.softTagName && !/^[a-zA-Z0-9]+$/i.test(values.softTagName)) {
      notification('Soft tag name must be alphanumeric.', 'destructive');
      return;
    }
    setSoftTagDetails((prev: SoftTagState | null) => ({ ...prev, ...values }));
    setActiveStep(2);
    window.history.pushState({ step: 2 }, '');
  };

  // Handles confirmation of the second step and moves to next
  const handleSecondStep = (values: any) => {
    setSoftTagDetails((prev: SoftTagState | null) => ({ ...prev, ...values }));
    setActiveStep(3);
    window.history.pushState({ step: 3 }, '');
  };

  // Handles back navigation between steps
  const handleBack = () => {
    if (activeStep === 1) {
      navigate(routes.LIST_TAGS);
      return;
    }
    setActiveStep(prev => prev - 1);
  };

  // Handles final confirmation and save, then navigates away
  const handleConfirmAndSave = () => {
    if (!softTagDetails) return;
    if (isEditMode) {
      const updatedSoftTag = {
        interval: setSchedule(
          softTagDetails.schedule,
          softTagDetails.schedulingInterval
        ),
        execution_code: softTagDetails.executionCode,
        timeseries_id: softTagDetails.timeseriesId as string,
      }; // Placeholder for update logic
      dispatch(
        editSoftTag({
          id: Number(location.state?.softTagData.id),
          ...updatedSoftTag,
        })
      );
      navigate(routes.LIST_TAGS);
    } else {
      const softTag = mapCreateSoftTagStateToParams(
        softTagDetails as SoftTagState
      );
      dispatch(createSoftTag(softTag))
        .unwrap()
        .then(() => {
          const apiMessage = 'The soft tag was successfully created.';
          notification(apiMessage, 'success');
          navigate(routes.LIST_TAGS);
        })
        .catch(err => {
          const errors = Array(err?.response?.data.error);
          const apiMessage = errors[0];
          notification(apiMessage, 'destructive');
        });
    }
  };

  return (
    <div className="w-full">
      <div className="flex flex-row py-1 px-8 gap-2 items-center">
        <IconButton
          className="p-0"
          size="xs"
          icon="ArrowLeft"
          variant="ghost"
          onClick={() => navigate(routes.LIST_TAGS)}
        />
        <Headline size="2xs">Back to soft tags search</Headline>
      </div>
      <Separator
        height="100%"
        mode="light"
        orientation="horizontal"
        width="100%"
      />
      <div className="py-2 px-4">
        <Title size="n">
          {isEditMode ? 'Editing soft tag' : 'Create new soft tag'}
        </Title>
        <Caption size="s">{stepsText[activeStep - 1]}</Caption>
      </div>
      {/* Progress tracker for multi-step flow */}
      <div className="py-2">
        <ProgressTracker
          activeStep={activeStep}
          css={{
            width: '70%',
            margin: '0 auto',
          }}
          labelPosition="bottom"
          steps={progressTrackerSteps}
          variant="label"
        />
      </div>
      {/* Step content: Details, Formula, Review */}
      <div className="mt-8">
        {activeStep === 1 &&
          (getSoftTagLoading ? (
            <div className="w-[50%] m-auto">
              <EditSoftTagSkeleton />
            </div>
          ) : (
            <Details
              softTagDetails={softTagDetails}
              handleConfirm={handleFirstStep}
              handleBack={handleBack}
              isEditMode={isEditMode}
            />
          ))}
        {activeStep === 2 && (
          <Formula
            handleConfirm={handleSecondStep}
            handleBack={handleBack}
            softTagDetails={softTagDetails}
            isEditMode={isEditMode}
          />
        )}
        {activeStep === 3 && (
          <Review
            handleConfirmAndSave={handleConfirmAndSave}
            handleBack={handleBack}
            softTagDetails={softTagDetails}
          />
        )}
      </div>
    </div>
  );
};
